import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SectionTitle, Loading, StatusChip } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Lock, Pencil, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { MembershipBilling } from "@/components/MembershipBilling";

const MEM_LABEL = { none: "None", fresh_care: "Fresh Care", fresh_care_plus: "Fresh Care+", fresh_complete: "Fresh Complete" };

const BLANK = { name: "", phone: "", email: "", address: "", membership: "none", internal_notes: "" };

export default function Customers() {
  const { user } = useAuth();
  const canEdit = user.role === "owner" || user.role === "manager";
  const [customers, setCustomers] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);   // customer being edited (with id)
  const [billing, setBilling] = useState(null);  // customer whose membership auto-billing modal is open
  const [form, setForm] = useState(BLANK);

  const load = async () => { const { data } = await api.get("/customers"); setCustomers(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    await api.post("/customers", form);
    toast.success("Customer created");
    setOpen(false); setForm(BLANK); load();
  };

  const saveEdit = async () => {
    if (!edit.name) { toast.error("Name required"); return; }
    const { id, ...body } = edit;
    await api.patch(`/customers/${id}`, body);
    toast.success("Customer updated");
    setEdit(null); load();
  };

  if (!customers) return <Loading label="Loading customers" />;

  return (
    <div className="max-w-5xl">
      <SectionTitle sub="Customer records · internal notes never exposed to customers" right={
        <button className="sg-btn sg-btn-accent" onClick={() => setOpen(true)} data-testid="new-customer-btn"><Plus size={18} /> New Customer</button>
      }>Customers</SectionTitle>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="customers-list">
        {customers.map((c) => (
          <div key={c.id} className="sg-card p-5" data-testid={`customer-${c.id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-head text-lg font-bold">{c.name}</span>
              <div className="flex items-center gap-2">
                <StatusChip status={c.membership === "none" ? "unpaid" : "certified"} label={MEM_LABEL[c.membership]} />
                {canEdit && <button className="sg-btn" style={{ minHeight: 30, padding: "3px 8px" }} data-testid={`billing-customer-${c.id}`} onClick={() => setBilling(c)}><CreditCard size={13} /></button>}
                {canEdit && <button className="sg-btn" style={{ minHeight: 30, padding: "3px 8px" }} data-testid={`edit-customer-${c.id}`} onClick={() => setEdit({ id: c.id, name: c.name || "", phone: c.phone || "", email: c.email || "", address: c.address || "", membership: c.membership || "none", internal_notes: c.internal_notes || "" })}><Pencil size={13} /></button>}
              </div>
            </div>
            <div className="text-sm" style={{ color: "var(--txt2)" }}>{c.address}</div>
            <div className="text-sm font-mono mt-1" style={{ color: "var(--txt2)" }}>{c.phone} · {c.email}</div>
            {"internal_notes" in c && c.internal_notes && (
              <div className="text-xs mt-3 p-2 rounded-lg flex items-start gap-1" style={{ background: "rgba(59,130,246,0.08)", color: "#93C5FD" }}>
                <Lock size={12} className="mt-0.5" /> Internal: {c.internal_notes}
              </div>
            )}
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setOpen(false)}>
          <div className="sg-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="new-customer-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-head text-2xl font-bold">New Customer</h3><button onClick={() => setOpen(false)}><X size={22} /></button></div>
            {[["name", "Name"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"]].map(([k, l]) => (
              <div key={k} className="mb-3">
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>{l}</label>
                <input className="sg-input mt-1" value={form[k]} data-testid={`cust-${k}`} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Membership</label>
            <select className="sg-input mt-1 mb-4" value={form.membership} data-testid="cust-membership" onChange={(e) => setForm((f) => ({ ...f, membership: e.target.value }))}>
              {Object.entries(MEM_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button className="sg-btn sg-btn-accent w-full" onClick={create} data-testid="create-customer-submit">Create Customer</button>
          </div>
        </div>
      )}

      {edit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setEdit(null)}>
          <div className="sg-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="edit-customer-modal">
            <div className="flex items-center justify-between mb-4"><h3 className="font-head text-2xl font-bold">Edit Customer</h3><button onClick={() => setEdit(null)}><X size={22} /></button></div>
            {[["name", "Name"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"]].map(([k, l]) => (
              <div key={k} className="mb-3">
                <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>{l}</label>
                <input className="sg-input mt-1" value={edit[k]} data-testid={`edit-cust-${k}`} onChange={(e) => setEdit((f) => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Membership</label>
            <select className="sg-input mt-1 mb-3" value={edit.membership} data-testid="edit-cust-membership" onChange={(e) => setEdit((f) => ({ ...f, membership: e.target.value }))}>
              {Object.entries(MEM_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <label className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>Internal notes</label>
            <input className="sg-input mt-1 mb-4" value={edit.internal_notes} data-testid="edit-cust-internal_notes" onChange={(e) => setEdit((f) => ({ ...f, internal_notes: e.target.value }))} />
            <button className="sg-btn sg-btn-accent w-full" onClick={saveEdit} data-testid="save-customer-edit">Save Changes</button>
          </div>
        </div>
      )}
      {billing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setBilling(null)}>
          <div className="sg-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="membership-billing-modal">
            <div className="flex items-center justify-between mb-1"><h3 className="font-head text-2xl font-bold">Membership Auto-Billing</h3><button onClick={() => setBilling(null)}><X size={22} /></button></div>
            <div className="text-sm mb-4" style={{ color: "var(--txt2)" }}>{billing.name}{billing.email ? ` · ${billing.email}` : ""}</div>
            <MembershipBilling customerId={billing.id} onChange={load} />
          </div>
        </div>
      )}
    </div>
  );
}
